/**
 * Polyfill for `URL.canParse()` — covers Safari <17, Chrome <120, Firefox <115.
 *
 * `@braintree/sanitize-url@7.1.0` calls `URL.canParse(url)` unconditionally
 * inside its internal `isValidUrl()` helper (introduced in upstream PR #77
 * alongside back-slash attack-vector handling). On older browsers this throws
 * `TypeError: URL.canParse is not a function`, which silently breaks the
 * 3DS redirect flow in `src/utils/redirect.ts` (the only consumer of
 * `sanitizeUrl` in this codebase).
 *
 * `URL.canParse` was added to the WHATWG URL spec in 2023:
 *   - Safari 17.0 (Sept 2023)
 *   - Chrome / Edge 120 (Dec 2023)
 *   - Firefox 115 (Jul 2023)
 *
 * `core-js` ships a polyfill since v3.30, but CRA 5 (react-scripts 5) does
 * not inject DOM polyfills and we are on core-js 3.28. We inline a minimal
 * polyfill here that matches the WHATWG signature `canParse(url, base?)`.
 *
 * This file MUST be the first import in `src/index.tsx` so the polyfill is
 * installed on the global `URL` object before any user code (including
 * `sanitizeUrl`) runs.
 */
export function canParseURL(url: string | URL, base?: string | URL): boolean {
  try {
    // eslint-disable-next-line no-new
    new URL(url, base)
    return true
  } catch {
    return false
  }
}

if (!('canParse' in URL)) {
  // @ts-expect-error -- polyfill assigns to readonly static URL method
  URL.canParse = canParseURL
}
